'use client';

import React from 'react';

import { HelpIconTooltip } from '@/components/tooltip/help-icon';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { StreamlineCheck } from '@tac/icons/functional/streamline-check';
import { DiscoveryFieldTitle } from '@/components/discovery-questions-input/field-title';
import { Field } from '@/components/ui/field';

import { LIFESTYLE_LABELS, TOGGLE_KEYS } from './constants';
import type { LifestylePreferenceTogglesProps } from './types';

const TOOLTIP_COPY =
  'Choose a lifestyle preference to help narrow your results. P-plater approved shows vehicles you can legally drive on your Ps. Safe cars for families highlights vehicles with good protection for children and rear-seat passengers.';

export const LifestylePreferenceToggles = ({
  pPlaterApproved,
  setPPlaterApproved,
  safeCarsForFamilies,
  setSafeCarsForFamilies,
}: LifestylePreferenceTogglesProps) => {
  const value = [
    ...(pPlaterApproved ? ['pPlaterApproved'] : []),
    ...(safeCarsForFamilies ? ['safeCarsForFamilies'] : []),
  ];

  const handleValueChange = (next: string[]) => {
    setPPlaterApproved(next.includes('pPlaterApproved'));
    setSafeCarsForFamilies(next.includes('safeCarsForFamilies'));
  };

  return (
    <Field className="w-full">
      <DiscoveryFieldTitle className="flex items-center gap-2" aria-label="Select your lifestyle preferences">
        <span>Select your lifestyle preferences</span>
        <span className="size-6 shrink-0 overflow-visible">
          <HelpIconTooltip content={TOOLTIP_COPY} />
        </span>
      </DiscoveryFieldTitle>
      <ToggleGroup
        type="multiple"
        value={value}
        onValueChange={handleValueChange}
        className="flex w-full flex-col gap-3 md:flex-row md:flex-wrap md:items-stretch"
      >
        {TOGGLE_KEYS.map((key) => {
          const { label } = LIFESTYLE_LABELS[key];
          return (
            <ToggleGroupItem
              key={key}
              value={key}
              className="flex w-full items-center justify-between md:min-w-0 md:flex-1 md:basis-0 md:w-auto"
            >
              {value.includes(key) && <StreamlineCheck aria-hidden className="size-4" />}
              <span className="min-w-0 flex-1 break-words whitespace-normal">{label}</span>
            </ToggleGroupItem>
          );
        })}
      </ToggleGroup>
    </Field>
  );
};

export type { LifestylePreferenceTogglesProps } from './types';
